import { Component, inject, PLATFORM_ID, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { finalize } from 'rxjs/operators';
import { ResetPasswordError } from '../reset-password-error/reset-password-error';
import { AuthService } from '../../../../../services/auth-service';
import { ResetPasswordDTO } from '../../../../../services/auth-service.model';

@Component({
  selector: 'app-reset-password-form',
  imports: [ReactiveFormsModule, CommonModule, ResetPasswordError],
  templateUrl: './reset-password-form.html',
  styleUrl: './reset-password-form.scss',
})
export class ResetPasswordForm {
  private authService = inject(AuthService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private username = signal<string>('');
  private resetOtp = signal<string>('');
  errorMessage = signal<string | null>(null);
  loading = signal<boolean>(false);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.username.set(localStorage.getItem('forgotUsername') || '');
      this.resetOtp.set(localStorage.getItem('resetOtp') || '');
      if (!this.username() || !this.resetOtp()) {
        this.router.navigate(['/login'], { replaceUrl: true });
      }
    }
  }

  form = new FormGroup({
    newPassword: new FormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(50),
      Validators.pattern(/^(?=.*\d)(?=.*[a-zA-Z])(?=.*[!@#$%^&*(){}[\]\-_=+\/\\~.<>:;"'])[a-zA-Z0-9!@#$%^&*(){}[\]\-_=+\/\\~.<>:;"']{8,50}$/),
    ]),
    confirmPassword: new FormControl('', [Validators.required]),
  });

  showNewPassword = false;
  showConfirmPassword = false;

  onSubmit() {
    if (this.form.invalid || this.form.controls.newPassword.value !== this.form.controls.confirmPassword.value) {
      this.form.markAllAsTouched();
      return;
    }
    this.errorMessage.set(null);
    this.loading.set(true);
    const newPasswordValue = this.form.controls.newPassword.value!;
    const payload: ResetPasswordDTO = {
      newPassword: newPasswordValue,
      otp: this.resetOtp(),
    };
    this.authService.resetPassword(this.username(), payload).pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: () => {
        this.router.navigate(['/reset-password-success'], { replaceUrl: true });
      },
      error: (err) => {
        this.errorMessage.set(err.messageEn || 'An unexpected error occurred. Please try again later.');
      },
    });
    if (this.errorMessage()) {
      this.form.reset();
      return;
    }
  }

  private errorCodeToMessage(code: string): string {
    return 'An unexpected error occurred. Please try again later.';
  }
}
